<template>
  <div class="workspace-cliente-detail-page workspace-clientes-page" :style="workspaceAccentStyle">
    <div class="modern-header shrink-0">
      <div class="header-container">
        <div class="breadcrumb-section">
          <nav class="breadcrumb">
            <button type="button" class="breadcrumb-item" @click="$router.push('/dashboard')">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
              {{ t('common.breadcrumb.dashboard') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <button type="button" class="breadcrumb-item" @click="goBack">
              {{ backBreadcrumbLabel }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{ t('workspaceClienteModal.headerTitleEdit') }}</span>
          </nav>
        </div>

        <div class="title-section">
          <div class="title-content">
            <div class="title-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
            </div>
            <div class="title-text">
              <h1 class="main-title">{{ clienteNome || t('workspaceClienteModal.headerTitleEdit') }}</h1>
              <p class="subtitle">{{ t('workspaceClienteModal.subtitle') }}</p>
            </div>
          </div>
        </div>

        <div class="actions-section">
          <button type="button" class="action-button secondary" @click="goBack">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
            {{ backButtonLabel }}
          </button>
        </div>
      </div>
    </div>

    <div class="workspace-cliente-detail-page__body page-content">
      <ClienteDetailModal
        v-if="metaReady"
        as-page
        :is-open="true"
        :cliente-id="clienteId"
        :workspace-id="workspaceId"
        :workspace-tags="workspaceTags"
        :workspace-users="workspaceUsers"
        :motivos-agendamento="motivosAgendamento"
        @close="goBack"
        @updated="onClienteUpdated"
        @archived="goBack"
        @title-change="clienteNome = $event"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import { useWorkspaceWhatsAppWebSocket } from '@/composables/useWorkspaceWhatsAppWebSocket'
import { useWorkspaceAccentStyle } from '@/composables/useWorkspaceAccentStyle'
import ClienteDetailModal from '@/components/ClienteDetailModal.vue'
import { mapWorkspaceUsers } from '@/utils/mapWorkspaceUsers'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const workspaceId = computed(() => Number(route.params.id))
const clienteId = computed(() => Number(route.params.clienteId))

useWorkspaceWhatsAppWebSocket(workspaceId)
const { workspaceAccentStyle } = useWorkspaceAccentStyle()

const workspaceTags = ref([])
const workspaceUsers = ref([])
const motivosAgendamento = ref([])
const clienteNome = ref('')
const metaReady = ref(false)

const isFromFinanceiro = computed(() => route.query.from === 'financeiro')
const isFromAgenda = computed(() => route.query.from === 'agenda')

const backBreadcrumbLabel = computed(() => {
  if (isFromFinanceiro.value) {
    return t('workspaceFinanceiroServicos.breadcrumbCurrent')
  }
  if (isFromAgenda.value) {
    return t('workspaceAgenda.breadcrumbCurrent')
  }
  return t('workspaceClientes.breadcrumbCurrent')
})

const backButtonLabel = computed(() => {
  if (isFromFinanceiro.value) {
    return t('workspaceClientes.backToFinanceiro')
  }
  if (isFromAgenda.value) {
    return t('workspaceClientes.backToAgenda')
  }
  return t('workspaceClientes.backToList')
})

const goBack = () => {
  if (isFromFinanceiro.value) {
    router.push({ name: 'WorkspaceFinanceiro', params: { id: workspaceId.value } })
    return
  }
  if (isFromAgenda.value) {
    router.push({ name: 'WorkspaceAgenda', params: { id: workspaceId.value } })
    return
  }
  const q = { ...route.query }
  delete q.from
  router.push({
    name: 'WorkspaceClientes',
    params: { id: workspaceId.value },
    query: q
  })
}

const onClienteUpdated = () => {
  /* lista recarrega ao voltar */
}

const loadMeta = async () => {
  try {
    const [configRes, usersRes, motivosRes] = await Promise.all([
      api.get(`/workspace-config/${workspaceId.value}/debug`),
      api.get(`/workspace-acesso/workspace/${workspaceId.value}`),
      api.get(`/workspace-config/${workspaceId.value}/motivos-agendamento`)
    ])
    workspaceTags.value = configRes.data?.categorias || []
    workspaceUsers.value = mapWorkspaceUsers(usersRes.data || [])
    motivosAgendamento.value = motivosRes.data || []
  } catch (error) {
    console.warn('Meta cliente detalhe:', error)
  }
}

onMounted(async () => {
  if (!clienteId.value || Number.isNaN(clienteId.value)) {
    goBack()
    return
  }
  await loadMeta()
  metaReady.value = true
})
</script>

<style src="@/assets/styles/workspace-clientes.css"></style>
<style scoped>
.workspace-cliente-detail-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
}
.workspace-cliente-detail-page__body {
  flex: 1 1 auto;
  min-height: calc(100vh - 13rem);
  min-height: calc(100dvh - 13rem);
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.workspace-cliente-detail-page__body :deep(.cliente-detail-page-root) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.modern-header .breadcrumb-section,
.modern-header .title-section,
.modern-header .actions-section,
.modern-header .header-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}
.modern-header .header-container { flex-direction: column; align-items: stretch; }
.modern-header .title-section { width: 100%; }
.modern-header .title-content { display: flex; gap: 16px; align-items: center; }
.modern-header .main-title { font-size: 1.75rem; font-weight: 700; margin: 0; }
.modern-header .subtitle { margin: 4px 0 0; color: var(--text-secondary); font-size: 14px; }
.modern-header .actions-section { display: flex; gap: 10px; flex-wrap: wrap; }
.action-button { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 4px; font-size: 14px; font-weight: 600; cursor: pointer; border: 1px solid var(--border-primary); }
.action-button.secondary { background: var(--bg-secondary); color: var(--text-primary); }
.breadcrumb { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13px; }
.breadcrumb-item { display: inline-flex; align-items: center; gap: 6px; color: var(--text-secondary); background: none; border: none; cursor: pointer; }
.breadcrumb-current { color: var(--text-primary); font-weight: 600; }
.breadcrumb-separator { color: var(--text-muted); }
</style>
